<template>
  <el-dialog
    ref="productDialog"
    title="Product Search"
    :visible.sync="visible"
    width="80%"
    append-to-body
    :close-on-click-modal="false"
    custom-class="opp-product-search-dialog"
  >
    <!-- 搜索行：整宽 input，回车或点放大镜按 keyword 搜索（照 EndCustomer 信息行之上新增，占据整个宽度） -->
    <div class="search-bar" ref="searchBar">
      <el-input
        class="keyword-inp"
        size="mini"
        v-model="keyword"
        placeholder="Enter at least 2 characters."
        @keyup.enter.native="handleSearch">
      </el-input>
      <div class="btn-with-icon-with-bg" @click="handleSearch">
        <span class="iconfont icon-spyglass">&#xe622;</span>
      </div>
    </div>
    <!-- 信息行（参考 EndCustomer）：左侧动态结果数，右侧刷新图标（去掉 Ordering Customer Name） -->
    <div class="info-bar" ref="infoBar">
      <div class="info-text">
        <span>{{ list.length }} result{{ list.length>1?'s':'' }}</span>
        <template v-if="searchedKeyword">
          <span class="dot"> • </span>
          <span>Filtered by 【{{ searchedKeyword }}】</span>
        </template>
      </div>
    </div>
    <!-- 展示后端按 keyword 返回的产品列表；单击选中、双击直接确认 -->
    <div class="table-wrap" v-loading="loading">
      <el-table
        ref="table"
        class="cqm-table"
        highlight-current-row
        size="mini"
        :height="tableHeight"
        style="width: 100%"
        :data="list"
        row-key="id"
        @row-click="handleRowClick"
        @cell-dblclick="handleDblClick"
      >
        <el-table-column
          align="center"
          width="70"
          label="">
          <template slot-scope="scope">
            <span class="iconfont icon-check icon-active" v-show="selected && selected.id==scope.row.id">&#xe730;</span>
            <span class="iconfont icon-check" v-show="!selected || selected.id!=scope.row.id">&#xe72f;</span>
          </template>
        </el-table-column>
        <el-table-column
          align="left"
          type="index"
          label=""
          width="50">
        </el-table-column>
        <el-table-column
          align="left"
          prop="productName"
          label="Product Description">
        </el-table-column>
        <el-table-column
          prop="model"
          label="Model">
        </el-table-column>
        <el-table-column
          width="150"
          prop="ean"
          label="EAN10">
        </el-table-column>
      </el-table>
    </div>
    <div slot="footer" class="dialog-footer">
      <div class="dialog-cancel" @click="close">Cancel</div>
      <div class="dialog-select" @click="handleSelect">Select</div>
    </div>
  </el-dialog>
</template>

<script>
import {api_opp_product_search} from '@/api/api'
import {getAction} from '@/api/tool'

export default {
  name:'ProductSearch',
  data(){
    return {
      visible:false,
      loading:false,
      keyword:'', //搜索关键字（输入框实时值），与后端交互的参数名为 keyword
      searchedKeyword:'', //实际生效的搜索词：仅在一次搜索请求成功、结果落地后更新，对应当前展示的 list（避免输入框已改但未回车时误显未生效的关键字）
      list:[],
      tableHeight:0, //表格固定高(px)，由 handleResize 量弹窗内容区得出（代替 height="100%"）
      selected:null //当前选中的行
    }
  },
  methods:{
    /* 打开弹窗：每次都是全新的空状态——不带入外部查询参数、不自动勾选、不自动发请求；
       搜索词与结果一律清空，无论外部是否有值（永远如此）。 */
    open(){
      this.visible=true
      this.selected=null
      //重置搜索词、生效搜索词与结果，避免保留上一次的搜索内容
      this.keyword=''
      this.searchedKeyword=''
      this.list=[]
      this.$nextTick(()=>{
        if(this.$refs.table){
          this.$refs.table.setCurrentRow(null)
        }
        // 弹窗显示后再量高（隐藏时 offsetHeight 为 0），并监听窗口变化
        this.handleResize()
        window.addEventListener('resize', this.handleResize)
      })
    },
    close(){
      this.visible=false
      window.removeEventListener('resize', this.handleResize)
    },
    /* 计算表格固定高：量弹窗内容区(.el-dialog__body)可用高，扣除搜索行与信息行，用数值高替代 height="100%" */
    handleResize(){
      this.$nextTick(()=>{
        let dialogEl = this.$refs.productDialog && this.$refs.productDialog.$el
        if(!dialogEl) return
        let bodyEl = dialogEl.querySelector('.el-dialog__body')
        let bodyH = bodyEl ? bodyEl.clientHeight : 0
        let searchH = this.$refs.searchBar ? this.$refs.searchBar.offsetHeight : 0
        let infoH = this.$refs.infoBar ? this.$refs.infoBar.offsetHeight : 0
        let h = bodyH - searchH - infoH //扣除搜索行与信息行高度，剩余给表格
        this.tableHeight = h > 100 ? h : 100
        if(this.$refs.table){
          this.$refs.table.doLayout()
        }
      })
    },
    handleSearch(){
      if(this.keyword.length>=2){
        this.getList()
      }else{
        this.$message.warning('Please enter at least 2 characters.')
      }
    },
    /* 按 keyword 拉取产品列表（GET，参数 keyword）；至少 2 个字符才请求，不足则清空结果且不发请求。
       后端可能直接返回数组或 {records:[]}，做兼容。用发请求时的 kw 快照，仅在成功回调里把 searchedKeyword 更新为 kw，
       确保“Filtered by”与当前结果对应，而不是跟随用户已改动但尚未回车的输入框实时值。 */
    getList(){
      let kw = (this.keyword || '').trim()
      //不足 2 个字符不请求，同时清空上一次的生效搜索词与结果
      if(kw.length < 2){
        this.list=[]
        this.searchedKeyword=''
        this.selected=null
        if(this.$refs.table){
          this.$refs.table.setCurrentRow(null)
        }
        return
      }
      this.loading=true
      getAction(api_opp_product_search, { keyword: kw }).then(res=>{
        this.loading=false
        if(Array.isArray(res)){
          this.list=res
        }else{
          this.list=(res && (res.records || res.list)) || []
        }
        this.searchedKeyword = kw //结果落地后才更新生效搜索词
      }).catch(()=>{
        this.loading=false
      })
    },
    /* 单击行选中（highlight-current-row 负责高亮，这里记录选中项供 Select 按钮使用） */
    handleRowClick(row){
      this.selected=row
    },
    /* 双击行直接确认返回 */
    handleDblClick(row){
      this.emitSelect(row)
    },
    /* 点击 Select 按钮确认 */
    handleSelect(){
      if(!this.selected){
        this.$message.warning('Please select a product first.')
        return
      }
      this.emitSelect(this.selected)
    },
    /* 把选中行抛给父组件并关闭弹窗 */
    emitSelect(row){
      this.$emit('select',{ ...row })
      this.close()
    }
  },
  beforeDestroy(){
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style lang="less">
@import url('/public/css/public.less');
/*
  本弹窗用 append-to-body：Element 内部节点(.el-dialog__header/__body/__footer)被搬到 <body> 下、
  不带组件 data-v，scoped(含 ::v-deep)命中不到，故整块走非 scoped 全局样式；
  组件自身模板元素(.search-bar/.info-bar/.table-wrap/.dialog-footer)也都在该弹窗内，一并嵌到
  custom-class="opp-product-search-dialog" 命名空间下，既保证生效又不污染全站。
  样式照抄 EndCustomer.vue(.opp-endcustomer-dialog)。
*/
.opp-product-search-dialog{
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  height: 80vh;
  max-height: 80vh;
  box-sizing: border-box;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,.3);
  .el-dialog__header{
    flex-shrink: 0;
    padding: 16px;
    text-align: left; /* 标题左对齐 */
    box-sizing: border-box;
    .el-dialog__title{
      line-height: 24px;
      font-size: 17px;
      color: @mainFontColor;
      font-weight: 600;
    }
    .el-dialog__headerbtn{
      top: 16px;
      right: 16px;
      .el-dialog__close{
        color: @inpNumColor;
        font-weight: 600;
      }
      &:hover .el-dialog__close{ color: @tableNoAndBtnColor; }
    }
  }
  .el-dialog__body{
    flex: 1;
    min-height: 0;
    padding: 0; /* 去掉留白，内容撑满整个内容区 */
    overflow: hidden;
    font-size: 13px;
    color: @mainFontColor;
  }
  .el-dialog__footer{
    flex-shrink: 0;
    padding: 16px;
    text-align: right;
    box-sizing: border-box;
    background-color: @darkBgcColor;
  }
  /* ===== 以下为组件自身模板元素，同处命名空间内 ===== */
  /* 搜索行：input 占满剩余宽度 + 右侧放大镜按钮触发搜索 */
  .search-bar{
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid @darkBorderColor;
    background-color: @darkBgcColor;
    padding: 12px;
    box-sizing: border-box;
    .keyword-inp{
      flex: 1;
      min-width: 0;
    }
    .btn-with-icon-with-bg{
      flex-shrink: 0;
      margin-left: 8px;
    }
  }
  /* 信息行：左侧动态结果数，右侧刷新图标 */
  .info-bar{
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background-color: @darkBgcColor;
    box-sizing: border-box;
    border-top: 1px solid @darkBorderColor;
    .info-text{
      font-size: 12px;
      color: @mainFontColor;
    }
  }
  .table-wrap{
    /* 表格外观走全局公共类 .cqm-table(public/css/public2.less)，此处只留本弹窗专有样式 */
    .el-table.cqm-table{
      border-radius: 0;
    }
    /* 选中行淡橙底，便于辨识当前选择 */
    .el-table__body tr.current-row > td.el-table__cell{
      background-color: fade(@mainColor, 10%);
    }
    /* select 列勾选图标(未选 e72f 灰、已选 e730 橙) */
    .icon-check{
      font-size: 17px;
      color: #ccc;
      &:hover{
        cursor: pointer;
      }
    }
    .icon-active{
      color: @mainColor;
    }
  }
  /* 底部 Cancel/Select 用全局公共类 .dialog-cancel/.dialog-select(public2.less)，此处只留右对齐 */
  .dialog-footer{
    text-align: right;
  }
}
</style>
